import { pwr } from './types.js';

export const MAX_LEVEL = 20;
export const LEVEL_DOUBLING_SPAN = 5.5;
export const MAX_XP = 18430;
export const VICTORY_XP_MULTIPLIER = Object.freeze({
  easy: 0.7,
  normal: 1.0,
  wrath: 1.6,
  mp: 1.0,
});
export const LEVEL_MILESTONES = [
  { level: 5, key: 'awakened', label: 'HOLO' },   // v10: 'Awakened' is now the battle state
  { level: 10, key: 'forged', label: 'FORGED' },
  { level: 15, key: 'exalted', label: 'EXALTED' },
  { level: 20, key: 'maxed', label: 'MAXED' },
];

export function clampLevel(level) {
  return Math.max(1, Math.min(MAX_LEVEL, Math.trunc(Number(level) || 1)));
}
export function growthAtLevel(level) {
  return 2 ** ((clampLevel(level) - 1) / LEVEL_DOUBLING_SPAN);
}
export function scaleStat(value, level) {
  return Math.max(1, Math.round((Number(value) || 0) * growthAtLevel(level)));
}
export function speedAtLevel(value, level) {
  return Math.max(1, Math.round(Number(value) || 0) + Math.floor((clampLevel(level) - 1) / 4));
}
export function xpForNextLevel(level) {
  const l = clampLevel(level);
  if (l >= MAX_LEVEL) return 0;
  const n = l - 1;
  return 100 + 35 * n + 5 * n * n;
}
export function totalXpForLevel(level) {
  let total = 0;
  for (let l = 1; l < clampLevel(level); l++) total += xpForNextLevel(l);
  return total;
}
export function levelFromXp(xp) {
  const total = Math.max(0, Math.min(MAX_XP, Math.trunc(Number(xp) || 0)));
  let level = 1, used = 0;
  while (level < MAX_LEVEL && used + xpForNextLevel(level) <= total) {
    used += xpForNextLevel(level++);
  }
  return level;
}
export function levelProgress(xp) {
  const total = Math.max(0, Math.min(MAX_XP, Math.trunc(Number(xp) || 0)));
  const level = levelFromXp(total);
  if (level >= MAX_LEVEL) {
    return { level, current: 0, needed: 0, remaining: 0, ratio: 1, maxed: true };
  }
  const floor = totalXpForLevel(level);
  const needed = xpForNextLevel(level);
  const current = Math.max(0, total - floor);
  return {
    level, current, needed, remaining: Math.max(0, needed - current),
    ratio: needed ? Math.max(0, Math.min(1, current / needed)) : 1,
    maxed: false,
  };
}
export function milestoneAtLevel(level) {
  const l = clampLevel(level);
  return [...LEVEL_MILESTONES].reverse().find(m => l >= m.level) || null;
}
export function nextMilestone(level) {
  const l = clampLevel(level);
  return LEVEL_MILESTONES.find(m => m.level > l) || null;
}
export function effectivePwr(card, level = 1) {
  return Math.max(1, Math.round(pwr(card) * growthAtLevel(level)));
}
export function levelCard(card, level = 1) {
  const l = clampLevel(level);
  return {
    ...card,
    level: l,
    hp: scaleStat(card.hp, l),
    spd: speedAtLevel(card.spd, l),
    moves: (card.moves || []).map(m => ({ ...m, dmg: scaleStat(m.dmg, l) })),
  };
}
export function teamMedianEffectivePwr(ids, byId, levels = {}) {
  const values = ids.map(id => effectivePwr(byId[id], levels[id] || 1)).sort((a, b) => a - b);
  if (!values.length) return 0;
  const m = Math.floor(values.length / 2);
  return values.length % 2 ? values[m] : (values[m - 1] + values[m]) / 2;
}
export function victoryXp(enemyMedianPwr, difficulty = 'normal', mode = 'solo') {
  const base = Math.max(40, Math.min(500, 40 + 0.6 * (Number(enemyMedianPwr) || 0)));
  const tier = mode === 'mp' ? 'mp' : difficulty;
  const multiplier = VICTORY_XP_MULTIPLIER[tier] ?? 1.0;
  return Math.round(base * multiplier);
}

// v10 HOLO STATUS. Legendary and above are born Holo; everything else earns it
// at HOLO_LEVEL. Level 5 = 680 XP on the curve above; a win pays a fielded card
// ~50-64 XP at NORMAL (victoryXp), so a regularly used card earns Holo in about
// 11-14 wins. It is the old level-5 milestone, relabelled HOLO (Awakened is now a battle state).
export const HOLO_LEVEL = 5;
export const HOLO_XP = 680;            // totalXpForLevel(HOLO_LEVEL); pinned for the server
const HOLO_BORN = new Set(['legendary', 'mythic', 'eternal']);
export function holoBorn(card) { return !!card && HOLO_BORN.has(card.rarity); }
export function isHolo(card, level = 1) { return holoBorn(card) || clampLevel(level) >= HOLO_LEVEL; }
